import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { Form, Field, Formik } from 'formik';
import { get, isEmpty } from 'lodash';
import { Spinner, SpinnerSize, PrimaryButton } from 'office-ui-fabric-react';
import { Dialog, DialogType, DialogFooter } from 'office-ui-fabric-react/lib/Dialog';
import { DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { fetchUsersByRoles, useUserSelectorByRoles } from '../../actions/users';
import useAPI from '../../hooks/useAPI';
import { can, getUser } from '../../utils/auth';
import StackGrid from '../StackGrid';
import TextField from '../TextField';
import SearchBar from '../SearchBar';
import styles from './ReviewerSelectionDialog.style';

const ReviewerRoles = [
  'businessProcessingManager',
  'royaltyOperationsManager',
  'contractProcessingManager',
];

const ReviewerSelectionDialog = ({
  contractAddress,
  usersUploadedAttachments,
  onSubmit,
  onDismiss,
  isOpen,
  className,
}) => {
  const metadata = useSelector(state =>
    get(state, `entities.contracts.${contractAddress}.metadata`, null),
  );

  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState({});
  const [showUserSuggestions, setShowUserSuggestions] = useState(true);

  const { data: reviewers } = useAPI(() => fetchUsersByRoles(ReviewerRoles), null, {
    disabled: !can('viewUsers'),
    fetchOnMount: true,
    selector: useUserSelectorByRoles(ReviewerRoles)
      .filter(
        reviewer =>
          user.profile.oid !== reviewer.oid &&
          reviewer.displayName &&
          reviewer.oid &&
          !usersUploadedAttachments.includes(reviewer.oid),
      )
      .map(reviewer => ({
        key: reviewer.oid,
        text: reviewer.displayName,
      })),
  });

  useEffect(() => {
    if (isEmpty(selectedUser)) {
      const approver = {
        value: get(metadata, 'businessProcessingManager.oid', metadata?.businessProcessingManager),
        label: get(
          metadata,
          'businessProcessingManager.displayName',
          metadata?.businessProcessingManager,
        ),
      };
      setSelectedUser(approver);
    }
  }, [metadata]);

  const handleSearch = async sValue => {
    setLoading(true);
    const searchedReviewers = reviewers.filter(reviewer => {
      return reviewer.text.toLowerCase().includes(sValue.toLowerCase());
    });
    setSearchResults(searchedReviewers);

    setShowUserSuggestions(true);
    setLoading(false);
  };

  const onUserSelection = approver => {
    setSelectedUser(approver);
    setShowUserSuggestions(false);
  };

  const handleClear = () => {
    setSelectedUser('');
    setShowUserSuggestions(false);
  };

  const getSuggestions = () => {
    if (!showUserSuggestions) {
      return null;
    }
    const suggestion = searchResults
      .map(reviewer => ({
        icon: 'People',
        value: reviewer.key,
        label: reviewer.text,
      }))
      .slice(0, 5);
    return suggestion;
  };
  let defaultReviewer = metadata.businessProcessingManager;

  // need to update
  if (!defaultReviewer?.oid) {
    defaultReviewer = {
      oid: metadata.businessProcessingManager,
      displayName: metadata.businessProcessingManager,
    };
  }

  const initial = {
    reviewer: defaultReviewer,
    comment: '',
  };

  const message = `In order to share the attachments with Partner, these attachments needs to be reviewed & approved. A default approver is assigned by
  the system (see below drop down). If you would like to change the approver please use the options available in the dropdown. A comment is
  needed if the approver is being changed from default.`;

  const submitHandler = async values => {
    const formValues = {
      Id: selectedUser?.value,
      comment: values.comment,
    };

    await onSubmit(formValues);
  };

  const validateFormValues = formValues => {
    const errors = {};
    if (!selectedUser?.value) {
      errors.reviewer = 'Required';
    }

    if (selectedUser?.value !== defaultReviewer.oid && !formValues.comment) {
      errors.comment = 'Required';
    }
    return errors;
  };

  return (
    <Dialog
      dialogContentProps={{
        type: DialogType.normal,
        title: 'Attachment Approver',
        closeButtonAriaLabel: 'Close',
      }}
      modalProps={{ className: clsx(styles.dialog, className) }}
      hidden={!isOpen}
      onDismiss={onDismiss}
      onSubmit={onSubmit}
    >
      <Formik
        validateOnChange={false}
        validateOnBlur={false}
        validate={validateFormValues}
        onSubmit={submitHandler}
        initialValues={initial}
      >
        {({ isSubmitting }) => {
          return (
            <Form>
              <Label className={styles.dialogLabel}>{message}</Label>
              <div className={styles.dialogContent}>
                <StackGrid smCols={1} mdCols={2} lgCols={2}>
                  <Label>
                    Approver <span className={styles.required}>*</span>
                    <SearchBar
                      className={styles.search}
                      required
                      underlined
                      isVisible
                      placeholder="Search Approver"
                      ariaLabel="Search"
                      data-testid="search-user-input"
                      isLoading={loading}
                      onChange={handleSearch} // when you type
                      onClear={handleClear}
                      onClick={onUserSelection}
                      value={selectedUser.label}
                      suggestions={getSuggestions()}
                    />
                  </Label>
                  {!isEmpty(selectedUser) && selectedUser?.value !== defaultReviewer?.oid && (
                    <Field
                      name="comment"
                      label="Comment"
                      component={TextField}
                      required
                      multiline
                    />
                  )}
                </StackGrid>
              </div>
              <DialogFooter>
                <DefaultButton
                  data-testid="cancel-button"
                  onClick={onDismiss}
                  disabled={isSubmitting}
                >
                  Cancel
                </DefaultButton>
                <PrimaryButton type="submit" data-testid="submit-button" disabled={isSubmitting}>
                  Submit
                  {isSubmitting && (
                    <Spinner
                      data-testid="approval-spinner"
                      size={SpinnerSize.medium}
                      className={styles.loadingSpinner}
                    />
                  )}
                </PrimaryButton>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

ReviewerSelectionDialog.propTypes = {
  contractAddress: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  usersUploadedAttachments: PropTypes.array,
  isOpen: PropTypes.bool,
  className: PropTypes.string,
};

ReviewerSelectionDialog.defaultProps = {
  usersUploadedAttachments: [],
  isOpen: false,
  className: 'normal',
};

export default ReviewerSelectionDialog;
